import { Spinner, Tooltip, useToast } from "@chakra-ui/react";
import React, { useState } from "react";

import {
  BrokenLinkIcon,
  Button,
  errorToast,
  IconButton,
  LinkIcon,
  successToast,
} from "../../../components";
import { copy } from "../../../utils/clipboard";
import { useRenewAiInterviewInvitationMutation } from "../../graphql";
import useHover from "../../graphql/hooks/useHover";

type AiInterviewerInvitationLinkButtonProps = {
  invitationId: string;
  invitationLink: string;
  isInvitationExpired: boolean;
  canEdit?: boolean;
};

export const AiInterviewerInvitationLinkButton: React.FC<
  AiInterviewerInvitationLinkButtonProps
> = ({ invitationId, invitationLink, isInvitationExpired, canEdit }) => {
  const toast = useToast();

  const [el, setEl] = useState<HTMLButtonElement | null>(null);
  const isHovered = useHover(el);

  const [renewAiInterviewInvitation, { loading: isRenewing }] =
    useRenewAiInterviewInvitationMutation({
      variables: { invitationId },
    });

  const onCopy = () => {
    copy({
      plain: invitationLink,
      html: `<a href="${invitationLink}">${invitationLink}</a>`,
    });
    successToast(toast, "Link copied to clipboard");
  };

  if (!invitationLink) return null;

  if (isRenewing) {
    return (
      <Button
        size="sm"
        pointerEvents="none"
        variant="ghost"
        leftIcon={<Spinner size="sm" />}
      >
        Regenerating
      </Button>
    );
  }

  if (isInvitationExpired) {
    const onRenew = async () => {
      try {
        await renewAiInterviewInvitation();
        onCopy();
      } catch (error) {
        errorToast(toast, error.message);
      }
    };

    return (
      <Tooltip
        textAlign="center"
        label={
          canEdit ? (
            <>
              Invite expired.
              <br />
              Click to regenerate.
            </>
          ) : (
            "Invite expired"
          )
        }
        openDelay={200}
      >
        <IconButton
          ref={setEl}
          variant="ghost"
          size="sm"
          colorScheme="red"
          onClick={onRenew}
          isDisabled={!canEdit}
          icon={
            <BrokenLinkIcon
              transitionProperty="color"
              transitionDuration="normal"
              color="red.500"
            />
          }
          aria-label={canEdit ? "Regenerate link" : "Invite expired"}
        />
      </Tooltip>
    );
  }

  return (
    <Tooltip label="Copy invite link" openDelay={200}>
      <IconButton
        ref={setEl}
        variant="ghost"
        size="sm"
        onClick={onCopy}
        icon={
          <LinkIcon
            transitionProperty="color"
            transitionDuration="normal"
            color={isHovered ? "blue.600" : "gray.700"}
          />
        }
        aria-label="Copy link"
      />
    </Tooltip>
  );
};
